/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { useAppSelector } from '../../reducers';
import { getCurrentUser } from '../../selectors/user';
import { UtrvComment } from '../../types/universalTrackerValue';
import { UserMin } from '@g17eco/types/user';
import { naturalSort } from '../../utils';
import Comment from './comment';
import AddComment from './addComment';
import { SuggestionDataItem } from 'react-mentions';
import './styles.scss';
import { ExtendedUtrvComments } from '../../api/utrv-comments';
import IconButton from '@g17eco/molecules/button/IconButton';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';

interface UtrvCommentsProps {
  utrvId: string;
  toggle: () => void;
  commentId?: string;
  utrvComments: ExtendedUtrvComments | undefined;
  isLoading: boolean;
  surveyId: string;
}

type LocalUser = Pick<UserMin, '_id' | 'firstName' | 'surname'>;

const UtrvComments = ({ utrvId, toggle, commentId, utrvComments, isLoading, surveyId }: UtrvCommentsProps) => {
  const user = useAppSelector(getCurrentUser);
  const pageSizeIncrement = 3;
  const [pageSize, setPageSize] = useState(pageSizeIncrement);

  const hydrateUser = useCallback((item: UtrvComment, users: LocalUser[]) => {
    const user = users.find(u => u._id === item.userId);
    if (user) {
      return {
        _id: user._id,
        name: `${user.firstName} ${user.surname}`
      }
    }
    return {
      _id: item.userId,
      name: '-'
    };
  }, []);

  const users = useMemo(() => {
    if (!utrvComments) {
      return [];
    }
    return utrvComments.users;
  }, [utrvComments]);

  const comments = useMemo(() => {
    if (!utrvComments) {
      return [];
    }
    return [...utrvComments.items]
      .sort((a, b) => naturalSort(b.created, a.created))
      .map((item) => ({
        ...item,
        user: hydrateUser(item, utrvComments.users),
      }));
  }, [hydrateUser, utrvComments]);

  const mentionUsers: SuggestionDataItem[] = useMemo(() => users.map(user => ({ id: user._id, display: `${user.firstName} ${user.surname}` })), [users]);

  if (!user) {
    return null;
  }

  const visibleComments = commentId ? comments : comments.slice(0, pageSize);

  return <div className='utrv-comments pt-6'>
    <IconButton
      className='position-absolute top-0 end-0 mt-1 me-1'
      outline={false}
      color='transparent'
      icon='fal fa-close'
      onClick={toggle}
    />
    <AddComment users={mentionUsers} toggle={toggle} utrvId={utrvId} user={user} surveyId={surveyId} />
    <LoadingPlaceholder isLoading={isLoading} height={32} >
      <div className='p-2 text-ThemeTextPlaceholder text-right'>
        {comments.length} comments
      </div>
    </LoadingPlaceholder>
    <LoadingPlaceholder isLoading={isLoading} height={150}>
    {visibleComments.map((comment, i) => (
      <Comment
        isScrolledTo={comment._id === commentId}
        users={mentionUsers}
        key={`comment_${utrvId}-${comment._id}-${i}`}
        comment={comment}
        utrvId={utrvId}
        user={user}
        surveyId={surveyId}
      />
    ))}
    </LoadingPlaceholder>
    <LoadingPlaceholder isLoading={isLoading} height={32}>
      {comments.length > pageSize && visibleComments.length !== comments.length ? (
        <div className='mt-3 text-center'>
          <Button size='sm' onClick={() => setPageSize(pageSize + pageSizeIncrement)}>Load {Math.min(pageSizeIncrement, comments.length - pageSize)} More</Button>
          <div className='mt-3 text-ThemeTextMedium'>{comments.length - pageSize} previous comments</div>
        </div>
      ) : null}
    </LoadingPlaceholder>
  </div>
};

export default UtrvComments;
